import { useRef, useState, useMemo } from "react";
import { fmt } from "../lib/calc";
import { API } from "../lib/api";
import { useDrag } from "../context/DragContext";
import { Plus, Trash2, Layers, GripVertical, Upload, Loader2, ArrowDownAZ, Clock, Pencil, Eye, X, ExternalLink, Eraser, Undo2, Boxes, Archive } from "lucide-react";
import { DrawingViewer } from "./DrawingViewer";

export function PanelQueue({ panels, onAdd, onDelete, onEdit, onImportFile, onClearAll, onUndo, canUndo, onAutoPackJob, onOpenBank, importing, placements, loads, variant = "desktop" }) {
  const sfx = variant === "mobile" ? "-mobile" : "";
  const { startDrag } = useDrag();
  const fileRef = useRef(null);
  const [sortMode, setSortMode] = useState("job");
  const [jobFilter, setJobFilter] = useState("all");
  const [viewPanel, setViewPanel] = useState(null);

  const isPlaced = (id) => placements.some((p) => p.panelId === id);
  const isLoaded = (id) => loads.some((l) => l.panelIds.includes(id));

  const jobs = useMemo(
    () => [...new Set(panels.map((p) => p.jobNumber).filter(Boolean))].sort(),
    [panels]
  );

  const visible = useMemo(
    () => (jobFilter === "all" ? panels : panels.filter((p) => (p.jobNumber || "No Job #") === jobFilter)),
    [panels, jobFilter]
  );
  const totals = useMemo(
    () => ({
      n: visible.length,
      vol: visible.reduce((s, p) => s + (p.volume || 0), 0),
      mass: visible.reduce((s, p) => s + (p.mass || 0), 0),
    }),
    [visible]
  );

  const groups = useMemo(() => {
    if (sortMode === "added") return [{ job: null, items: visible }];
    const sorted = [...visible].sort(
      (a, b) => (a.jobNumber || "~").localeCompare(b.jobNumber || "~") || a.name.localeCompare(b.name)
    );
    const map = [];
    sorted.forEach((p) => {
      const job = p.jobNumber || "No Job #";
      const last = map[map.length - 1];
      if (last && last.job === job) last.items.push(p);
      else map.push({ job, items: [p] });
    });
    return map;
  }, [visible, sortMode]);

  const clearScope = jobFilter === "all" ? null : jobFilter;

  const loadStats = useMemo(() => {
    const inLoad = new Set(loads.flatMap((l) => l.panelIds || []));
    const placed = new Set(placements.map((p) => p.panelId));
    const onTable = visible.filter((p) => placed.has(p.id)).length;
    const assigned = visible.filter((p) => inLoad.has(p.id)).length;
    const unplaced = visible.filter((p) => !placed.has(p.id) && !inLoad.has(p.id)).length;
    return { onTable, assigned, unplaced };
  }, [visible, loads, placements]);

  const ghost = (p) => (
    <div className="px-3 py-2 border text-white shadow-2xl font-heading font-bold text-xs" style={{ backgroundColor: `${p.color}ee`, borderColor: p.color }}>
      {p.name}
      <span className="block text-[10px] font-mono font-normal opacity-90">
        {fmt(p.width)} × {fmt(p.length)} m
      </span>
    </div>
  );

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) onImportFile(f);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col h-full bg-[#16181A] border border-[#2E3033]">
      <div className="px-4 py-3 border-b border-[#2E3033] shrink-0 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Layers className="w-4 h-4 text-[#2560C9] shrink-0" />
            <h2 className="font-heading font-bold text-white text-sm tracking-tight">PANEL QUEUE</h2>
            <span className="text-[10px] font-mono text-[#6E7787] bg-[#0A0A0A] border border-[#2E3033] px-1.5 py-0.5">{panels.length}</span>
          </div>
          {canUndo && (
            <button
              data-testid={`undo-clear-button${sfx}`}
              onClick={onUndo}
              className="flex items-center gap-1 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-[10px] font-mono uppercase tracking-wider px-2 py-1 transition-colors shrink-0"
              title="Undo last clear"
            >
              <Undo2 className="w-3 h-3" /> Undo
            </button>
          )}
        </div>

        <input ref={fileRef} type="file" accept=".pdf,application/pdf" onChange={handleFile} className="hidden" data-testid={`import-file-input${sfx}`} />
        <div className="grid grid-cols-4 gap-1.5">
          <button
            data-testid={`add-panel-button${sfx}`}
            onClick={onAdd}
            className="flex items-center justify-center gap-1 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-2 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <button
            data-testid={`import-pdf-button${sfx}`}
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="flex items-center justify-center gap-1 border border-[#2E3033] hover:bg-[#22252A] text-white text-xs px-2 py-1.5 transition-colors disabled:opacity-60"
            title="Import panels from a shop-drawing PDF"
          >
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            PDF
          </button>
          <button
            data-testid={`from-bank-button${sfx}`}
            onClick={onOpenBank}
            className="flex items-center justify-center gap-1 border border-[#2E3033] hover:bg-[#22252A] text-white text-xs px-2 py-1.5 transition-colors"
            title="Add panels from the Drawing Bank"
          >
            <Archive className="w-3.5 h-3.5" /> Bank
          </button>
          <button
            data-testid={`clear-queue-button${sfx}`}
            onClick={() => {
              if (totals.n === 0) return;
              onClearAll(clearScope);
            }}
            disabled={totals.n === 0}
            className="flex items-center justify-center gap-1 border border-[#2E3033] text-[#A0AABF] hover:border-[#EF4444]/60 hover:text-[#EF4444] text-xs px-2 py-1.5 transition-colors disabled:opacity-40"
            title={clearScope ? `Clear job ${clearScope}` : "Clear all panels"}
          >
            <Eraser className="w-3.5 h-3.5" />
            <span className="truncate">{clearScope ? clearScope : "Clear"}</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-[#2E3033] shrink-0 flex-wrap">
        <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787] mr-1">Sort</span>
        <SortBtn active={sortMode === "job"} onClick={() => setSortMode("job")} testid={`sort-job${sfx}`} icon={ArrowDownAZ}>
          Job #
        </SortBtn>
        <SortBtn active={sortMode === "added"} onClick={() => setSortMode("added")} testid={`sort-added${sfx}`} icon={Clock}>
          Recent
        </SortBtn>
        <select
          data-testid={`job-filter${sfx}`}
          value={jobFilter}
          onChange={(e) => setJobFilter(e.target.value)}
          className="ml-auto bg-[#0A0A0A] border border-[#2E3033] text-[#A0AABF] text-[10px] font-mono uppercase tracking-wider px-2 py-1 focus:border-[#2560C9] focus:outline-none"
        >
          <option value="all" className="bg-[#0A0A0A]">All jobs</option>
          {jobs.map((j) => (
            <option key={j} value={j} className="bg-[#0A0A0A]">
              {j}
            </option>
          ))}
        </select>
        {clearScope && (
          <button
            data-testid={`autopack-job-button${sfx}`}
            onClick={() => onAutoPackJob(clearScope)}
            className="flex items-center gap-1 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-[10px] font-mono uppercase tracking-wider px-2 py-1 transition-colors"
            title={`Auto-pack ${clearScope} into loads`}
          >
            <Boxes className="w-3 h-3" /> Pack {clearScope}
          </button>
        )}
      </div>

      <div data-testid={`queue-summary${sfx}`} className="grid grid-cols-3 border-b border-[#2E3033] shrink-0 divide-x divide-[#2E3033]">
        <Stat label={clearScope ? clearScope : "Panels"} value={totals.n} testid={`summary-count${sfx}`} />
        <Stat label="Volume" value={`${fmt(totals.vol, 2)} m³`} testid={`summary-volume${sfx}`} />
        <Stat label="Mass" value={`${fmt(totals.mass / 1000, 2)} t`} testid={`summary-mass${sfx}`} />
      </div>
      <div className="grid grid-cols-3 border-b border-[#2E3033] shrink-0 divide-x divide-[#2E3033]">
        <Stat label="On Table" value={loadStats.onTable} color="#10B981" testid={`summary-ontable${sfx}`} />
        <Stat label="In Loads" value={loadStats.assigned} color="#F59E0B" testid={`summary-inloads${sfx}`} />
        <Stat label="Unplaced" value={loadStats.unplaced} color={loadStats.unplaced > 0 ? "#3B75DF" : "#10B981"} testid={`summary-unplaced${sfx}`} />
      </div>

      <div data-testid={`panel-queue-list${sfx}`} className="flex-1 overflow-y-auto p-3 space-y-3">
        {panels.length === 0 && (
          <div className="text-center text-[#6E7787] text-sm font-mono py-10 px-4 border border-dashed border-[#2E3033]">
            No panels yet.
            <br />
            <span className="text-[#3B75DF]">Add</span> one or import a <span className="text-[#3B75DF]">PDF</span>.
          </div>
        )}

        {groups.map((grp) => (
          <div key={grp.job || "all"} className="space-y-2">
            {grp.job && (
              <div className="flex items-center gap-2 sticky top-0 bg-[#16181A] py-1 z-10">
                <span className="text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-[#2560C9]">{grp.job}</span>
                <span className="text-[9px] font-mono text-[#6E7787]">{grp.items.length} panel{grp.items.length !== 1 ? "s" : ""}</span>
                <span className="flex-1 h-px bg-[#2E3033]" />
              </div>
            )}
            {grp.items.map((p) => (
              <div
                key={p.id}
                data-testid={`queue-panel${sfx}-${p.id}`}
                className="group relative bg-[#22252A] border border-[#2E3033] hover:border-[#2560C9]/60 transition-all"
              >
                <div className="flex items-stretch">
                  <div className="w-1.5 shrink-0" style={{ backgroundColor: p.color }} />
                  <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          data-testid={`panel-drag-handle${sfx}-${p.id}`}
                          onPointerDown={(e) => startDrag({ source: "queue", panelId: p.id }, ghost(p), e)}
                          className="cursor-grab active:cursor-grabbing text-[#6E7787] hover:text-[#2560C9] p-0.5 -ml-0.5 shrink-0"
                          style={{ touchAction: "none" }}
                          title="Drag to table or load"
                        >
                          <GripVertical className="w-4 h-4" />
                        </span>
                        <span
                          data-testid={`panel-number${sfx}-${p.id}`}
                          title={p.name}
                          className="font-heading font-bold text-white text-sm break-words leading-tight"
                        >
                          {p.name}
                        </span>
                        {p.jobNumber && (
                          <span className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 border border-[#2560C9]/50 text-[#3B75DF] shrink-0">
                            {p.jobNumber}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isPlaced(p.id) && <Tag color="#10B981">ON TABLE</Tag>}
                        {isLoaded(p.id) && <Tag color="#F59E0B">LOADED</Tag>}
                        {p.drawingId && (
                          <button
                            data-testid={`view-panel${sfx}-${p.id}`}
                            onClick={() => setViewPanel(p)}
                            className="text-[#6E7787] hover:text-[#10B981] transition-colors"
                            title="View drawing"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          data-testid={`edit-panel${sfx}-${p.id}`}
                          onClick={() => onEdit(p)}
                          className="text-[#6E7787] hover:text-[#2560C9] transition-colors"
                          title="Edit panel"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-panel${sfx}-${p.id}`}
                          onClick={() => onDelete(p.id)}
                          className="text-[#6E7787] hover:text-[#EF4444] transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 text-[11px] font-mono text-[#A0AABF] uppercase tracking-wide flex items-center gap-1.5">
                      {fmt(p.width)} × {fmt(p.length)} m · {p.shape}
                      {p.dimsEstimated && (
                        <span
                          data-testid={`dims-est-badge${sfx}-${p.id}`}
                          title="Footprint estimated from the drawing — confirm via Edit"
                          className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 border border-[#F59E0B]/50 text-[#F59E0B]"
                        >
                          Est
                        </span>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1.5">
                      <Data label="Thick" value={`${fmt(p.thickness, 3)} m`} />
                      <Data label="Area" value={`${fmt(p.area)} m²`} />
                      <Data label="Vol" value={`${fmt(p.volume, 3)} m³`} />
                      <Data label="Mass" value={`${fmt(p.mass / 1000, 2)} t`} />
                      <Data label="f'c" value={`${fmt(p.strength28)} MPa`} />
                      <Data label="Shape" value={p.shape} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {viewPanel && (
        <div
          data-testid="drawing-modal"
          onClick={() => setViewPanel(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-[#16181A] border border-[#2E3033]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: viewPanel.color }} />
                <h3 className="font-heading font-bold text-white text-sm truncate">{viewPanel.name}</h3>
                {viewPanel.jobNumber && (
                  <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 border border-[#2560C9]/50 text-[#3B75DF]">
                    {viewPanel.jobNumber}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <a
                  data-testid="drawing-open-newtab"
                  href={`${API}/panels/drawing/${viewPanel.drawingId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-[#A0AABF] hover:text-white text-xs font-mono"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <button data-testid="drawing-modal-close" onClick={() => setViewPanel(null)} className="text-[#6E7787] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#0A0A0A] p-3">
              <DrawingViewer
                src={`${API}/panels/drawing/${viewPanel.drawingId}`}
                alt={`Drawing ${viewPanel.name}`}
                maxHeight="100%"
                testid="drawing-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, color }) {
  return (
    <div className="px-3 py-2 text-center" data-testid={testid}>
      <p className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#6E7787] leading-none truncate">{label}</p>
      <p className="text-sm font-mono mt-1 leading-none" style={{ color: color || "#ffffff" }}>{value}</p>
    </div>
  );
}

function SortBtn({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors ${
        active ? "bg-[#2560C9] border-[#2560C9] text-white" : "border-[#2E3033] text-[#A0AABF] hover:bg-[#22252A]"
      }`}
    >
      <Icon className="w-3 h-3" />
      {children}
    </button>
  );
}

function Data({ label, value }) {
  return (
    <div>
      <p className="text-[9px] font-mono uppercase tracking-[0.1em] text-[#6E7787] leading-none">{label}</p>
      <p className="text-[11px] font-mono text-white mt-0.5 leading-none truncate">{value}</p>
    </div>
  );
}

function Tag({ children, color }) {
  return (
    <span className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 border" style={{ color, borderColor: `${color}66` }}>
      {children}
    </span>
  );
}
